import { api } from './api';

type SignInRequestData = {
  username: string;
  password: string;
  apiKey?: string;
  grant_type?: string;
};

const APIKEY =
  !!(window as any).FUNIFIER_ENV?.apikey && typeof (window as any).FUNIFIER_ENV?.apikey === 'string'
    ? (window as any).FUNIFIER_ENV?.apikey
    : process.env.REACT_APP_APIKEY;

export function signInRequest(data: SignInRequestData) {
  data['apiKey'] = APIKEY;
  data['grant_type'] = 'password';

  return api
    .post('/auth/token', data)
    .then(response => {
      return response.data;
    })
    .catch(error => {
      // console.error(error.response.data.message);
      return error.response.data;
    });
}

export function fetchUser() {
  return api
    .get('/player/me/status')
    .then(response => {
      const user = response.data;
      if (user) {
        return user;
      } else {
        document.dispatchEvent(new CustomEvent('signout'));
      }
    })
    .catch(error => {
      // console.log(error);
    });
}

export function registerUser(user: any) {
  return api
    .put('/database/registration__c', user, {
      headers: {
        Authorization: 'Basic ' + btoa(`${APIKEY}:${process.env.REACT_APP_REGISTRATION_SECRETKEY}`),
      },
    })
    .then(response => {
      const user = response.data;
      return user;
    })
    .catch(error => {
      // console.log(error);
    });
}

export async function recoverPasswordRequest(data: SignInRequestData) {
  data['apiKey'] = APIKEY;
  data['grant_type'] = 'password';

  try {
    const response = await api.post('/auth/token', data);
    return response.data.access_token;
  } catch (error) {
    console.log(error);
  }
}

// A plataforma recebe senha e código pela query string. Como o formulário exige caractere
// especial, encodar deixa de ser detalhe: sem isso `#` trunca a URL (o `code` some), `&`
// cria outro parâmetro e `+` vira espaço — e a troca falha sem explicação.
function buildQuery(params: Record<string, string>) {
  return Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value ?? '')}`)
    .join('&');
}

export async function changePassword(data: any) {
  const query = buildQuery({
    player: data._id,
    old_password: data.old_password,
    new_password: data.new_password,
  });

  try {
    const response = await api.put(`/player/password?${query}`);
    if (response.data.errorCode) {
      throw new Error('Houve um problema ao atualizar a senha. Por favor, tente novamente.');
    }
    return response.data;
  } catch (error) {
    throw new Error('Houve um problema ao atualizar a senha. Por favor, tente novamente.');
  }
}

export function getRoles(userId: string) {
  return api
    .get(`/database/principal?q=_id:"${userId}"`)
    .then(response => {
      const role = response.data[0];
      if (role) {
        return role;
      } else {
        document.dispatchEvent(new CustomEvent('signout'));
      }
    })
    .catch(error => {
      // console.log(error);
    });
}

export type RecoveryRequest = {
  /** Houve código gerado — só então faz sentido pedir o código ao jogador. */
  accepted: boolean;
  /** Motivo cru devolvido pela plataforma, quando ela recusa. */
  message?: string;
};

// A plataforma responde 201 mesmo quando NÃO gera código; o motivo vem apenas no corpo.
const RECOVERY_REJECTIONS = ['player does not exist', 'player does not have email'];

// O envio SMTP nativo desta instância falha e a plataforma devolve 500 "Third party
// error" — mas só depois de já ter gravado o código, que nasce válido. Quem entrega é o
// trigger `password_change_message`, com o scheduler `password_recovery_send` como
// reconciliação. Logo, esse 500 não significa jogador sem código: significa que o caminho
// nativo, que não usamos, falhou. Tratar como falha travava o fluxo na primeira tela.
function isNativeDeliveryFailure(error: any) {
  return error?.response?.status === 500 && error?.response?.data?.errorMessage === 'Third party error';
}

export async function recoverPassword(username: string): Promise<RecoveryRequest> {
  const player = username.toLocaleLowerCase().trim();

  try {
    // O interceptor já aplica o Basic apiKey nas rotas `player/password`.
    const { data } = await api.get(`/player/password/change?player=${encodeURIComponent(player)}`);
    const message: string | undefined = typeof data?.message === 'string' ? data.message : undefined;
    const rejected = !!message && RECOVERY_REJECTIONS.some(reason => message.includes(reason));

    return { accepted: !rejected, message };
  } catch (error) {
    if (isNativeDeliveryFailure(error)) {
      return { accepted: true };
    }
    throw error;
  }
}

export async function updatePassword(payload: { username: string; password: string; code: string }) {
  const { username, password, code } = payload;
  const query = buildQuery({
    player: username.toLocaleLowerCase().trim(),
    new_password: password,
    code,
  });

  const { data } = await api.put(`/player/password?${query}`);
  return data?.type === 'OK';
}
